import { computed, inject, Injectable, signal } from '@angular/core';
import { NotificationsService } from '../../notifications/notifications.service';
import { HttpClient } from '@angular/common/http';
import {
    EntityRequestDTO,
    RequestResearcherReviewDTO,
    ResearchEntityRequestStatus,
} from '../../shared/interfaces/request';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, Subject, tap } from 'rxjs';
import { EsploroFileModel } from '../../shared/interfaces/research-portal/full-asset-page/asset-model/asset-model-sections/esploro-file-model.interface';
import { Asset } from '../../shared/interfaces/assets-search-results.interface';
import { RestApiBase } from '../../shared/configurations/rest-api-base.config';
import { JwtUtilService } from '../../core/jwt-util.service';
import { RequestApprovalDTO } from '../../shared/interfaces/request/request-approval-dto';
import { arraysEqual } from '@exl-ng/mulo-core';

@Injectable({
    providedIn: 'root',
})
export class RequestsService {
    private notifySvc = inject(NotificationsService);
    private http = inject(HttpClient);
    private jwtSvc = inject(JwtUtilService);

    readonly _allRequests = signal<EntityRequestDTO[]>([], {
        equal: arraysEqual,
    });
    private readonly _assets = signal<{ [id: string]: Asset }>({});
    private readonly _files = signal<{ [id: string]: EsploroFileModel }>({});
    private readonly _arNames = signal<{ [id: string]: string }>({});
    private readonly _arDefaultExpireDays = signal<{ [id: string]: number }>(
        {},
    );

    readonly allRequests = this._allRequests.asReadonly();
    readonly pendingRequests = computed(() =>
        this.allRequests().filter(
            (request) => request.status === ResearchEntityRequestStatus.Pending,
        ),
    );
    readonly assets = this._assets.asReadonly();
    readonly files = this._files.asReadonly();
    readonly arNames = this._arNames.asReadonly();
    readonly arDefaultExpireDays = this._arDefaultExpireDays.asReadonly();

    private dataRefreshSubject = new Subject<void>();

    constructor() {
        toSignal(
            this.getRequestData().pipe(
                tap((requests) => this._allRequests.set(requests)),
            ),
        );
        this.notifySvc.addStream('requests', this.pendingRequests);
    }

    getAvailableAccessRightsForRequest() {
        return this.http.get<string[]>(
            RestApiBase.FileRequest + '/availableAccessRightsForRequest',
        );
    }

    getFileRequestPolicy(accessRightId: string) {
        return this.http.get(
            RestApiBase.FileRequest + '/policy/' + accessRightId,
            { responseType: 'text' },
        );
    }

    createFileRequest(requestDTO: EntityRequestDTO) {
        return this.http.post(RestApiBase.FileRequest + '/create', requestDTO);
    }

    updateData() {
        this.dataRefreshSubject.next();
    }

    getRequestData() {
        const userId = this.jwtSvc.getUserID();
        return this.http
            .get<RequestResearcherReviewDTO>(
                RestApiBase.FileRequest + `/pendingApproval/${userId}`,
            )
            .pipe(
                filter((res) => !!res),
                tap((res) => {
                    if (res?.mmsIdToEntityModelMap) {
                        this._assets.update((prev) => ({
                            ...prev,
                            ...res.mmsIdToEntityModelMap,
                        }));
                    }
                    if (res?.midToFileModelMap) {
                        this._files.update((prev) => ({
                            ...prev,
                            ...res.midToFileModelMap,
                        }));
                    }
                    if (res?.accessRightIdToPolicyNameMap) {
                        this._arNames.update((prev) => ({
                            ...prev,
                            ...res.accessRightIdToPolicyNameMap,
                        }));
                    }
                    if (res?.accessRightIdToDefaultExpirationDaysMap) {
                        this._arDefaultExpireDays.update((prev) => ({
                            ...prev,
                            ...res.accessRightIdToDefaultExpirationDaysMap,
                        }));
                    }
                }),
                map((data) => data.requests),
            );
    }

    approve(approvalData: RequestApprovalDTO) {
        return this.http
            .post<EntityRequestDTO>(
                RestApiBase.FileRequest + '/approve',
                approvalData,
            )
            .pipe(
                tap((updatedReq) => {
                    this._allRequests.update((prev) => {
                        const index = prev.findIndex(
                            (req) => req.requestId === updatedReq.requestId,
                        );
                        const updated = [...prev];
                        if (index !== -1) {
                            updated[index] = { ...prev[index], ...updatedReq };
                        }
                        return updated;
                    });
                }),
            );
    }
}
